import { Platform } from "react-native"

import * as Device from "expo-device"
import * as Notifications from "expo-notifications"
import Constants from "expo-constants"

import { sendRequest } from "./send-request"
import { makeFileLogger } from "./logger"

const fileLogger = makeFileLogger({ fileName: "push-notifications.ts" })

/**
 * Configure how notifications are handled when app is in the foreground.
 */
export function configureForegroundHandler(): void {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  })
}

/**
 * Create "FarmHQ Alerts" channel for Android 8+.
 */
export async function configureAndroidChannel(): Promise<void> {
  if (Platform.OS === "android") {
    await Notifications.setNotificationChannelAsync("farmhq-alerts", {
      name: "FarmHQ Alerts",
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: "#4CAF50",
    })
  }
}

/**
 * Get the Expo push token for this device.
 * Returns null on web or simulator.
 */
export async function getExpoPushToken(): Promise<string | null> {
  if (Platform.OS === "web") {
    return null
  }

  if (!Device.isDevice) {
    fileLogger.warn("Push notifications are not available on simulator")
    return null
  }

  const { status: existingStatus } =
    await Notifications.getPermissionsAsync()
  let finalStatus = existingStatus

  if (existingStatus !== "granted") {
    const { status } = await Notifications.requestPermissionsAsync()
    finalStatus = status
  }

  if (finalStatus !== "granted") {
    fileLogger.warn("Push notification permission not granted")
    return null
  }

  const projectId = Constants.expoConfig?.extra?.eas?.projectId
  if (!projectId) {
    fileLogger.error("Missing EAS projectId for push notifications")
    return null
  }

  const tokenData = await Notifications.getExpoPushTokenAsync({ projectId })
  return tokenData.data
}

/**
 * Register the push token with the backend.
 */
export async function registerTokenWithBackend(
  token: string,
): Promise<void> {
  await sendRequest("RegisterPushToken", {
    actionArguments: {
      token,
      platform: Platform.OS === "ios" || Platform.OS === "android" ? Platform.OS : null,
      deviceId: Device.modelId ?? null,
    },
    activeFarmId: undefined,
  })
}

/**
 * Unregister the push token from the backend.
 * Best-effort: does not throw on failure.
 */
export async function unregisterTokenFromBackend(
  token: string,
): Promise<void> {
  try {
    await sendRequest("UnregisterPushToken", {
      actionArguments: { token },
      activeFarmId: undefined,
    })
  } catch (error) {
    fileLogger.error(error)
  }
}
